import type { Booking, ChangeBookingStageInput } from '../api/bookings';
import type { BookingStage } from '../generated/enums';

/**
 * The decisions that move a booking along by hand — quote, hold, confirm, expire, cancel, recover —
 * offered only where the server's `IsLegalStageMove` allows them, for the website and the phone.
 *
 * <p>
 * <strong>`InProgress` and `Completed` are never targets.</strong> They follow from the garments,
 * and the server refuses them here; a hand-over and a return are their own calls.
 * </p>
 *
 * <p>
 * <strong>A hold always carries its hours.</strong> The server refuses a hold with none
 * (`HoldNeedsAnExpiry`), and the website's Hold button sent none until 2026-10-06 — so holding a
 * quotation for the evening could never succeed. {@link stageMoveInput} is now the only way either
 * client builds the request, and it cannot leave them out.
 * </p>
 */

/** The longest hold the server accepts — `BookingService.MaxHoldHours`, a week. */
export const maxHoldHours = 7 * 24;

/** What a hold lasts unless somebody says otherwise — the new booking's own default. */
export const defaultHoldHours = 24;

/**
 * `forward` moves the booking towards a hand-over; `exit` ends it, and is drawn apart from the
 * forward moves so the one pressed forty times a day is never beside the one that cancels a wedding.
 */
export type StageMoveKind = 'forward' | 'exit';

export interface StageMove {
  to: BookingStage;
  kind: StageMoveKind;
  /** The button. */
  label: string;
  /** What pressing it does, said before it is done. */
  consequence: string;
  /** Whether the screen asks why — optional on the server, and stored as the stage history's reason. */
  asksReason: boolean;
  /** The reason sent when nobody is asked: a lapse is the clock's decision, not a person's. */
  fixedReason: string | null;
}

const quote: StageMove = {
  to: 'Quotation',
  kind: 'forward',
  label: 'Quote',
  consequence:
    'Gives the booking a QT number. Nothing is set aside, so the garments stay free for anyone else.',
  asksReason: false,
  fixedReason: null,
};

const hold: StageMove = {
  to: 'Hold',
  kind: 'forward',
  label: 'Hold',
  consequence:
    'Sets the garments aside for a few hours without a number. It lapses on its own if nobody confirms.',
  asksReason: false,
  fixedReason: null,
};

const confirm: StageMove = {
  to: 'Confirmed',
  kind: 'forward',
  label: 'Confirm',
  consequence: 'Sets the garments aside for the dates and gives the booking its BK number.',
  asksReason: false,
  fixedReason: null,
};

const confirmHold: StageMove = {
  ...confirm,
  label: 'Confirm hold',
  consequence:
    'Keeps the garments already set aside and gives the booking its BK number. The hold stops lapsing.',
};

const expire: StageMove = {
  to: 'Expired',
  kind: 'exit',
  label: 'Mark expired',
  consequence:
    'The customer never confirmed. Frees the garments; it counts as lapsed, not as a cancellation.',
  asksReason: false,
  fixedReason: 'Lapsed without confirmation',
};

const recover: StageMove = {
  to: 'Recovery',
  kind: 'exit',
  label: 'Escalate to recovery',
  consequence:
    'For garments that are not coming back. What is still owed is written off into recovery.',
  asksReason: true,
  fixedReason: null,
};

const cancel: StageMove = {
  to: 'Cancelled',
  kind: 'exit',
  label: 'Cancel booking',
  consequence: 'Frees every garment on the booking. The number stays in the register as cancelled.',
  asksReason: true,
  fixedReason: null,
};

/** Every move the server allows from a stage, forward moves first — `IsLegalStageMove`, mirrored. */
export function stageMovesFrom(stage: BookingStage): StageMove[] {
  switch (stage) {
    case 'Enquiry':
      return [quote, hold, confirm, cancel];
    case 'Quotation':
      return [hold, confirm, expire, cancel];
    case 'Hold':
      return [confirmHold, expire, cancel];
    case 'Confirmed':
      return [cancel];
    case 'InProgress':
      return [recover];
    default:
      return [];
  }
}

/** The moves offered on this booking. */
export function stageMoves(booking: Pick<Booking, 'stage'>): StageMove[] {
  return stageMovesFrom(booking.stage);
}

/** The one move to `to` from here, or null when the server would refuse it. */
export function stageMoveTo(booking: Pick<Booking, 'stage'>, to: BookingStage): StageMove | null {
  return stageMoves(booking).find((move) => move.to === to) ?? null;
}

/** Why a hold's hours would be refused, or null when the server will take them. */
export function holdHoursProblem(hours: number): string | null {
  if (!Number.isInteger(hours) || hours <= 0) {
    return 'A hold with no expiry takes stock off the rail for a season.';
  }

  if (hours > maxHoldHours) {
    return `A hold can last at most ${maxHoldHours} hours — confirm the booking instead.`;
  }

  return null;
}

/**
 * The request for a move. Hours go only with a hold (the server refuses them on anything else), and
 * a blank reason is sent as nothing rather than as an empty string the history would keep.
 */
export function stageMoveInput(
  move: StageMove,
  answers: { reason?: string; holdHours?: number } = {},
): ChangeBookingStageInput {
  const typed = answers.reason?.trim() ?? '';

  return {
    stage: move.to,
    reason: move.fixedReason ?? (move.asksReason && typed !== '' ? typed : null),
    holdHours: move.to === 'Hold' ? (answers.holdHours ?? defaultHoldHours) : null,
  };
}
